/**
 * SubMetricSliderRow — single weighted metric row
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FormulaSubMetric } from '@trafficpulse/shared';

import { Slider } from '@/components/ui/form/controls';
import { Button } from '@/components/ui/layout/Button';

import { LowestToggle, useAllowLowest } from '@/features/flow-optimizer/tabs/calculation/sliders/lowest';

import { useFormulaMetricCatalog } from '../metric-catalog/FormulaMetricCatalogContext';

export interface SubMetricSliderRowProps {
  metric: FormulaSubMetric;
  onMetricChange: (next: string) => void;
  onWeightChange: (next: number) => void;
  onRemove: () => void;
  /** "Highest" / "Lowest" — shown only where the builder offers it (the Automation). */
  onLowestChange?: (lowest: boolean) => void;
}

const SubMetricSliderRow: FC<SubMetricSliderRowProps> = ({
  metric,
  onMetricChange,
  onWeightChange,
  onRemove,
  onLowestChange,
}) => {
  const metadata = useFormulaMetricCatalog();
  const metrics = metadata.data?.metrics ?? [];
  const allowLowest = useAllowLowest() && Boolean(onLowestChange) && Boolean(metric.metric);
  const name = metrics.find((m) => m.id === metric.metric)?.name ?? metric.metric;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={metric.metric}
        onChange={(e) => onMetricChange(e.target.value)}
        className="h-9 w-44 rounded-md border border-input bg-background px-2 text-xs"
      >
        <option value="">Pick metric…</option>
        {metrics.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
      {allowLowest ? <LowestToggle lowest={metric.lowest === true} name={name} onChange={(next) => onLowestChange?.(next)} /> : null}
      <Slider
        value={[metric.weight]}
        min={0}
        max={100}
        step={1}
        onValueChange={(values) => onWeightChange(values[0] ?? 0)}
        className="min-w-[140px] flex-1"
      />
      <span className="w-12 text-right text-xs font-semibold tabular-nums">
        {metric.weight}%
      </span>
      <Button
        size="sm"
        variant="ghost"
        type="button"
        onClick={onRemove}
        className="h-9 px-2 text-xs"
        aria-label="Remove sub-metric"
      >
        ×
      </Button>
    </div>
  );
};

export default SubMetricSliderRow;
